import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ShieldCheck, AlertTriangle, ArrowRight, Share2, Calendar, Code2, Copy } from "lucide-react";
import { toast } from "sonner";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function scoreTone(score) {
  if (score >= 70) return { label: "Buen cumplimiento", bg: "from-emerald-500 to-emerald-700", ring: "stroke-emerald-400", text: "text-emerald-200", badge: "bg-emerald-500" };
  if (score >= 40) return { label: "Por mejorar", bg: "from-amber-500 to-amber-700", ring: "stroke-amber-400", text: "text-amber-200", badge: "bg-amber-500" };
  return { label: "Requiere acción", bg: "from-rose-500 to-rose-700", ring: "stroke-rose-400", text: "text-rose-200", badge: "bg-rose-500" };
}

const IMP_BADGE = {
  alta: "bg-rose-500 text-white",
  media: "bg-amber-500 text-white",
  baja: "bg-slate-500 text-white",
};

export default function Score() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/score/${slug}`)
      .then(({ data }) => setData(data))
      .catch((err) => setError(err?.response?.status === 404 ? "404" : "Error al cargar"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-slate-500 text-sm">Cargando…</div>
    );
  }

  if (error === "404" || !data) {
    return (
      <div className="min-h-[60vh] px-6 py-20 max-w-2xl mx-auto text-center">
        <SEO title="Score no encontrado — Ley de Datos Chile" description="Este score público no existe o fue eliminado." canonical="https://leydedatoschile.com/score" />
        <p className="font-mono text-[10px] tracking-[0.22em] text-slate-500 uppercase mb-3">Error · 404</p>
        <h1 className="font-display text-4xl text-slate-900 tracking-tight">Score no encontrado</h1>
        <p className="mt-3 text-slate-600">Este resultado público no existe o fue retirado por su dueño.</p>
        <Link to="/" className="mt-8 inline-flex items-center gap-2 h-11 px-6 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold transition-colors">
          Escanea tu propio sitio gratis
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );
  }

  const tone = scoreTone(data.score);
  const fechaCorta = (data.fecha || "").slice(0, 10);
  const ogUrl = `${process.env.REACT_APP_BACKEND_URL}/api/score/${slug}/og.svg`;
  const pageUrl = `https://leydedatoschile.com/score/${slug}`;
  const shareText = encodeURIComponent(`Score de cumplimiento Ley 21.719 de ${data.nombre_publico}: ${data.score}/100`);
  const shareLn = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
  const shareWa = `https://wa.me/?text=${shareText}%20${encodeURIComponent(pageUrl)}`;

  return (
    <section className="px-6 py-12 sm:py-16" data-testid="public-score-page">
      <SEO
        title={`${data.nombre_publico} — Score Ley 21.719: ${data.score}/100 · Ley de Datos Chile`}
        description={`Score público de cumplimiento de la Ley 21.719 para ${data.nombre_publico}: ${data.score}/100. ${tone.label}. Escanea tu propio sitio gratis.`}
        canonical={pageUrl}
        image={ogUrl}
      />
      <div className="max-w-5xl mx-auto">
        {/* Hero card */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className={`relative overflow-hidden rounded-3xl bg-gradient-to-br ${tone.bg} text-white shadow-2xl p-8 sm:p-12`}
        >
          <div className="absolute inset-0 bg-grid-pattern opacity-20 pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase opacity-90 mb-5">
              <span className="live-dot inline-block" />
              Score público · Ley 21.719
            </div>
            <h1 data-testid="public-score-title" className="font-display text-4xl sm:text-5xl tracking-tight max-w-3xl leading-[1.05]">
              {data.nombre_publico}
            </h1>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-8 items-center">
              {/* Score ring */}
              <div className="relative w-44 h-44 sm:w-52 sm:h-52">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="8" />
                  <circle cx="50" cy="50" r="42" fill="none" className={tone.ring} strokeWidth="8" strokeLinecap="round" pathLength="100" strokeDasharray={`${data.score} 100`} />
                </svg>
                <div className="absolute inset-0 grid place-items-center">
                  <div className="text-center">
                    <div data-testid="public-score-value" className="font-display text-5xl sm:text-6xl tabular-nums">{data.score}</div>
                    <div className="font-mono text-[10px] tracking-[0.22em] uppercase opacity-80 mt-1">/ 100</div>
                  </div>
                </div>
              </div>

              <div>
                <div className={`inline-flex items-center gap-2 ${tone.badge} px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.18em]`}>
                  <ShieldCheck className="w-3 h-3" /> {tone.label}
                </div>
                <p className="mt-4 text-base sm:text-lg leading-relaxed text-white/90 max-w-2xl">
                  {data.resumen || "Resumen del escaneo no disponible."}
                </p>
                <div className="mt-5 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/70">
                  <Calendar className="w-3 h-3" />
                  Escaneado · {fechaCorta}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Hallazgos top */}
        {data.hallazgos_top && data.hallazgos_top.length > 0 && (
          <div className="mt-12">
            <p className="font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-3">
              ▸ Hallazgos destacados
            </p>
            <h2 className="font-display text-2xl sm:text-3xl text-slate-900 tracking-tight leading-tight inline-flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Top 2 puntos a mejorar
            </h2>
            <p className="mt-2 text-sm text-slate-500">
              El detalle completo es privado y queda en posesión del titular del sitio.
            </p>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {data.hallazgos_top.map((h, i) => (
                <article key={i} data-testid={`public-hallazgo-${i}`} className="rounded-2xl border border-slate-200 bg-white p-5 hover:shadow-sm transition-shadow">
                  <span className={`inline-block font-mono text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded ${IMP_BADGE[h.importancia] || IMP_BADGE.baja}`}>
                    {h.importancia || "baja"}
                  </span>
                  <h3 className="mt-3 font-display text-base text-slate-900">{h.titulo}</h3>
                </article>
              ))}
            </div>
          </div>
        )}

        {/* Share + CTA */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-6">
            <p className="font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-3 inline-flex items-center gap-1.5">
              <Share2 className="w-3 h-3" /> Compartir este score
            </p>
            <div className="flex flex-wrap gap-2">
              <a data-testid="share-linkedin" href={shareLn} target="_blank" rel="noopener noreferrer" className="h-10 px-4 rounded-full bg-[#0a66c2] hover:bg-[#0852a1] text-white text-sm font-semibold inline-flex items-center gap-2 transition-colors">LinkedIn</a>
              <a data-testid="share-whatsapp" href={shareWa} target="_blank" rel="noopener noreferrer" className="h-10 px-4 rounded-full bg-[#25d366] hover:bg-[#1ebd5d] text-white text-sm font-semibold inline-flex items-center gap-2 transition-colors">WhatsApp</a>
              <button data-testid="share-copy" onClick={() => navigator.clipboard?.writeText(pageUrl)} className="h-10 px-4 rounded-full border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-semibold transition-colors">Copiar link</button>
            </div>
          </div>

          <div className="rounded-2xl bg-ink-950 text-white p-6 flex flex-col justify-between relative overflow-hidden">
            <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
            <div className="relative">
              <p className="font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-2">¿Y tu sitio?</p>
              <h3 className="font-display text-xl tracking-tight">Escanea en 30 segundos.</h3>
              <p className="mt-2 text-sm text-white/70">
                3 análisis gratis al mes · sin registrarte.
              </p>
            </div>
            <Link to="/" className="relative mt-5 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-white text-slate-900 font-semibold w-fit hover:bg-slate-100 transition-colors">
              Escanear mi sitio gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <p className="mt-10 text-xs text-slate-400 max-w-2xl leading-relaxed">
          Este score público fue publicado con consentimiento del titular del sitio. El score se obtiene mediante un análisis automático de las señales técnicas visibles del sitio en la fecha indicada. No es una opinión legal y no reemplaza la revisión de un abogado.
        </p>

        {/* Opción B — Embed badge */}
        <EmbedBadgeSection slug={slug} pageUrl={pageUrl} />
      </div>
    </section>
  );
}

function EmbedBadgeSection({ slug, pageUrl }) {
  const badgeUrl = `${process.env.REACT_APP_BACKEND_URL}/api/score/${slug}/badge.svg`;
  const htmlSnippet = `<a href="${pageUrl}" target="_blank" rel="noopener" title="Cumplimiento Ley 21.719"><img src="${badgeUrl}" alt="Cumplimiento Ley 21.719 verificado por Ley de Datos Chile" width="320" height="44" loading="lazy"></a>`;
  const mdSnippet = `[![Cumplimiento Ley 21.719](${badgeUrl})](${pageUrl})`;

  const copy = (text, label) => {
    navigator.clipboard?.writeText(text);
    toast.success(`${label} copiado al portapapeles`);
  };

  return (
    <div data-testid="embed-badge-section" className="mt-12 rounded-2xl bg-[#0F172A] text-white p-6 sm:p-8">
      <div className="flex items-start gap-3">
        <Code2 className="w-5 h-5 text-blue-300 mt-1" />
        <div className="flex-1">
          <h3 className="font-display text-xl font-bold">Embebe tu sello en el footer de tu sitio</h3>
          <p className="mt-1 text-sm text-white/70 max-w-2xl">
            Muestra a tus clientes que cumples la Ley 21.719. Compatible con WordPress, Shopify, Wix, Webflow y cualquier sitio que acepte HTML.
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-6 items-start">
        {/* Preview real del badge */}
        <div className="rounded-xl bg-white/5 p-5 border border-white/10">
          <p className="text-[10px] uppercase tracking-wider font-semibold text-white/50 mb-3">Así se verá</p>
          <img
            data-testid="embed-badge-preview"
            src={badgeUrl}
            alt="Vista previa del badge"
            width={320}
            height={44}
            className="block"
          />
        </div>

        {/* Snippets */}
        <div className="space-y-4">
          <SnippetBlock label="HTML (para footer de sitios)" code={htmlSnippet} onCopy={() => copy(htmlSnippet, "HTML")} testid="snippet-html" />
          <SnippetBlock label="Markdown (para README, blog)" code={mdSnippet} onCopy={() => copy(mdSnippet, "Markdown")} testid="snippet-md" />
          <SnippetBlock label="URL directa del badge" code={badgeUrl} onCopy={() => copy(badgeUrl, "URL")} testid="snippet-url" />
        </div>
      </div>
    </div>
  );
}

function SnippetBlock({ label, code, onCopy, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-white/60">{label}</span>
        <button onClick={onCopy} className="text-xs font-semibold text-blue-300 hover:text-white inline-flex items-center gap-1">
          <Copy className="w-3 h-3" /> Copiar
        </button>
      </div>
      <pre className="bg-black/40 border border-white/10 rounded-lg p-3 text-xs text-white/90 font-mono whitespace-pre-wrap break-all">{code}</pre>
    </div>
  );
}
